import { useEffect, useSyncExternalStore } from "react";
import { primeListenCue } from "./listenCue";

/** Matches the composers' voice-input language. */
const SPEECH_LANG = "en-US";

/**
 * Chrome pauses `speechSynthesis` after roughly 15s and never resumes.
 * Calling `resume()` on a shorter interval keeps a long answer talking.
 */
const RESUME_INTERVAL_MS = 10_000;

/**
 * Chrome drops an utterance passed to `speak()` in the same turn as `cancel()`.
 * Waiting one task lets the previous read-aloud stop before the next starts.
 */
const SPEAK_DELAY_MS = 0;

/**
 * Default en-US voices are often local, robotic engines (Chrome/Edge on Windows
 * fall back to "Microsoft David/Zira Desktop"). {@link selectEnglishVoice}
 * instead ranks the installed voices so the most natural English one is chosen:
 * cloud "Natural"/"Neural" voices (Edge), Google US English (Chrome), and Apple
 * enhanced/premium voices (Safari) win over the robotic desktop fallbacks.
 */
/** Case-insensitive name fragments that mark a more natural-sounding voice. */
const NATURAL_VOICE_HINTS = [
  "online (natural)",
  "natural",
  "neural",
  "wavenet",
  "neural2",
  "google",
  "siri",
  "enhanced",
  "premium",
  "samantha",
  "ava",
  "allison",
  "nicky",
  "zoe",
  "microsoft aria",
  "microsoft jenny",
  "microsoft guy",
] as const;

/** Case-insensitive name fragments that mark a robotic, low-quality voice. */
const ROBOTIC_VOICE_HINTS = [
  "desktop",
  "microsoft david",
  "microsoft zira",
  "microsoft mark",
  "microsoft sam",
  "compact",
  "espeak",
  "fred",
] as const;

/**
 * Chrome often leaves `getVoices()` empty until `voiceschanged`. Waiting only
 * a couple of hundred ms lets the first greeting speak on the robotic OS
 * default (David/Zira on Windows) before Google/Natural voices appear.
 */
export const VOICE_WAIT_MS = 1500;

/** How often the slider estimates position when the browser sends no word boundaries. */
const PROGRESS_INTERVAL_MS = 100;

/**
 * English speech at the default rate covers about fifteen characters a second.
 * Chrome's Google US English voice does not emit `boundary` events, so the
 * slider follows this clock and only snaps when a boundary does arrive.
 */
const CHARS_PER_SECOND = 15;

export interface SpeechPlayback {
  /** Whether this browser exposes speechSynthesis. */
  isSupported: boolean;
  /** False when markdown cleanup leaves nothing worth saying. */
  canSpeak: boolean;
  /** True while this entry owns the read-aloud session, including a pause. */
  speaking: boolean;
  /** True while this entry's utterance is talking. */
  playing: boolean;
  /** True while this entry is paused in place. */
  paused: boolean;
  /** True while this entry's session is open. Transport controls use this. */
  active: boolean;
  /** Fraction of the speakable text already reached, from 0 to 1. */
  progress: number;
  /** Start this entry, pause it, or resume it from the same word. */
  toggle: () => void;
  /** Jump to a fraction of this entry and keep playing. No effect when it is idle. */
  seek: (ratio: number) => void;
}

/** Shared read-aloud session. `progress` is 0 when nothing is being read. */
export interface SpeechSessionSnapshot {
  entryId: string | null;
  conversationId: string | null;
  paused: boolean;
  progress: number;
}

type Listener = () => void;

let speakingEntryId: string | null = null;
/** Chat that started the open read-aloud. Entry ids repeat across chats. */
let sessionConversationId: string | null = null;
/** Chat currently on screen. Leaving it holds playback without ending the session. */
let visibleConversationId: string | null = null;
let greetingSpeaking = false;
let generation = 0;
/** The user paused playback. The Chrome keepalive must not resume over that. */
let userPaused = false;
/** Paused because another chat is on screen. Distinct from a pause the user chose. */
let held = false;
/** Speakable text of the open read-aloud session. Empty when idle. */
let sessionText = "";
/** Index into `sessionText` where the current utterance started. */
let sessionOffset = 0;
/** 0..1 position within `sessionText`. */
let sessionProgress = 0;
let progressTimer: number | null = null;
/** `Date.now()` when the current time estimate started. */
let progressStartedAt = 0;
/** Absolute character index the current time estimate started from. */
let progressOrigin = 0;

const IDLE_SNAPSHOT: SpeechSessionSnapshot = {
  entryId: null,
  conversationId: null,
  paused: false,
  progress: 0,
};

let snapshot: SpeechSessionSnapshot = IDLE_SNAPSHOT;
let resumeTimer: number | null = null;
let voiceWaitTimer: number | null = null;
let voiceWaitListener: EventListener | null = null;
const listeners = new Set<Listener>();
const greetingListeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Publish a new snapshot only when the session the rows render has changed. */
function publish(): void {
  const entryId = speakingEntryId;
  const conversationId = entryId === null ? null : sessionConversationId;
  const paused = (userPaused || held) && entryId !== null;
  const progress = entryId === null ? 0 : sessionProgress;
  if (entryId === null && conversationId === null && !paused && progress === 0) {
    if (snapshot === IDLE_SNAPSHOT) {
      return;
    }
    snapshot = IDLE_SNAPSHOT;
    emit();
    return;
  }
  if (
    snapshot.entryId === entryId &&
    snapshot.conversationId === conversationId &&
    snapshot.paused === paused &&
    snapshot.progress === progress
  ) {
    return;
  }
  snapshot = { entryId, conversationId, paused, progress };
  emit();
}

function clearProgressClock(): void {
  if (progressTimer === null) {
    return;
  }
  window.clearInterval(progressTimer);
  progressTimer = null;
}

/** Stay short of the end until `onend`, so the thumb does not finish early. */
function progressFromChars(chars: number): number {
  if (sessionText.length === 0) {
    return 0;
  }
  const capped = Math.min(Math.max(0, chars), sessionText.length - 1);
  return capped / sessionText.length;
}

function tickProgress(): void {
  if (userPaused || held || speakingEntryId === null || sessionText.length === 0) {
    clearProgressClock();
    return;
  }
  const elapsedSeconds = (Date.now() - progressStartedAt) / 1000;
  const next = progressFromChars(progressOrigin + elapsedSeconds * CHARS_PER_SECOND);
  // A word boundary may already sit ahead of this estimate. Do not pull it back.
  if (next <= sessionProgress) {
    return;
  }
  sessionProgress = next;
  publish();
}

/** Estimate read-aloud position from elapsed time. No effect for a greeting or a pause. */
function startProgressClock(): void {
  clearProgressClock();
  if (userPaused || held || speakingEntryId === null || sessionText.length === 0) {
    return;
  }
  progressStartedAt = Date.now();
  progressOrigin = sessionProgress * sessionText.length;
  progressTimer = window.setInterval(tickProgress, PROGRESS_INTERVAL_MS);
}

function clearEntrySession(): void {
  clearProgressClock();
  speakingEntryId = null;
  sessionConversationId = null;
  held = false;
  sessionText = "";
  sessionOffset = 0;
  sessionProgress = 0;
}

function clampRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) {
    return 0;
  }
  if (ratio < 0) {
    return 0;
  }
  if (ratio > 1) {
    return 1;
  }
  return ratio;
}

/** Move an index back to the start of the word that contains it. */
function snapToWord(text: string, index: number): number {
  if (index <= 0) {
    return 0;
  }
  if (index >= text.length) {
    return text.length;
  }
  const previous = text.lastIndexOf(" ", index);
  if (previous === -1) {
    return 0;
  }
  const start = previous + 1;
  if (start >= text.length) {
    return text.length;
  }
  return start;
}

function emitGreeting(): void {
  for (const listener of greetingListeners) {
    listener();
  }
}

function clearResume(): void {
  if (resumeTimer === null) {
    return;
  }
  window.clearInterval(resumeTimer);
  resumeTimer = null;
}

function clearVoiceWait(): void {
  if (voiceWaitTimer !== null) {
    window.clearTimeout(voiceWaitTimer);
    voiceWaitTimer = null;
  }
  if (voiceWaitListener !== null) {
    window.speechSynthesis.removeEventListener("voiceschanged", voiceWaitListener);
    voiceWaitListener = null;
  }
}

/**
 * Empty the utterance queue and clear any lingering native pause. `cancel()`
 * alone drops the queue but leaves the synthesizer paused when a prior
 * `pauseSpeech()` paused it, so the next utterance would stay silent until the
 * keepalive resumes it. `resume()` after `cancel()` lets a new playback start
 * at once. Callers must have already confirmed `isSpeechSupported()`.
 */
function cancelSpeech(): void {
  const synthesis = window.speechSynthesis;
  synthesis.cancel();
  synthesis.resume();
}

/** Higher is more natural. Used only to rank English voices against each other. */
function scoreEnglishVoice(voice: SpeechSynthesisVoice): number {
  const name = voice.name.toLowerCase();
  let score = 0;
  for (const hint of NATURAL_VOICE_HINTS) {
    if (name.includes(hint)) {
      score += 100;
    }
  }
  for (const hint of ROBOTIC_VOICE_HINTS) {
    if (name.includes(hint)) {
      score -= 100;
    }
  }
  // Cloud voices (localService false) sound better than on-device engines.
  if (voice.localService === false) {
    score += 40;
  }
  const lang = voice.lang.toLowerCase();
  if (lang === "en-us") {
    score += 20;
  } else if (lang.startsWith("en")) {
    score += 10;
  }
  return score;
}

/**
 * The most natural installed English voice, or null when none is listed yet or
 * this browser exposes no voice list. Ranks every `en-*` voice and returns the
 * best, so Chrome picks Google US English, Edge picks a cloud "Natural" voice,
 * and Safari picks an enhanced/premium one instead of the robotic default.
 */
function selectEnglishVoice(): SpeechSynthesisVoice | null {
  const synthesis = window.speechSynthesis;
  if (typeof synthesis.getVoices !== "function") {
    return null;
  }
  const english = synthesis
    .getVoices()
    .filter((voice) => voice.lang.toLowerCase().startsWith("en"));
  let best: SpeechSynthesisVoice | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const voice of english) {
    const score = scoreEnglishVoice(voice);
    // Strictly greater keeps the first listed voice on a tie, which favors the
    // browser's own default ordering (and en-US already scores above en-GB).
    if (score > bestScore) {
      best = voice;
      bestScore = score;
    }
  }
  return best;
}

/** True when the voice list is already loaded, or this browser has no list at all. */
function voicesKnown(): boolean {
  const synthesis = window.speechSynthesis;
  if (typeof synthesis.getVoices !== "function") {
    return true;
  }
  return synthesis.getVoices().length > 0;
}

function applyNaturalVoice(utterance: SpeechSynthesisUtterance): void {
  const voice = selectEnglishVoice();
  if (voice !== null) {
    utterance.voice = voice;
  }
}

function speakPrepared(
  token: number,
  utterance: SpeechSynthesisUtterance,
  onFail: () => void,
): void {
  if (token !== generation) {
    return;
  }
  applyNaturalVoice(utterance);
  try {
    window.speechSynthesis.speak(utterance);
  } catch {
    onFail();
    return;
  }
  // A pause during the cancel-delay lands before `speak()`. Pause immediately
  // so that click still holds the new utterance instead of letting it play.
  if (userPaused) {
    window.speechSynthesis.pause();
    return;
  }
  startResume();
  startProgressClock();
}

/**
 * Speak after the cancel-delay, once the browser has had a chance to list its
 * voices (so {@link selectEnglishVoice} can pick a natural one). A newer
 * utterance or `stopSpeech` drops this one.
 */
function scheduleSpeak(
  token: number,
  utterance: SpeechSynthesisUtterance,
  onFail: () => void,
): void {
  window.setTimeout(() => {
    if (token !== generation) {
      return;
    }
    if (voicesKnown()) {
      speakPrepared(token, utterance, onFail);
      return;
    }
    if (typeof window.speechSynthesis.addEventListener !== "function") {
      speakPrepared(token, utterance, onFail);
      return;
    }
    const onVoices: EventListener = () => {
      if (!voicesKnown()) {
        return;
      }
      clearVoiceWait();
      speakPrepared(token, utterance, onFail);
    };
    voiceWaitListener = onVoices;
    window.speechSynthesis.addEventListener("voiceschanged", onVoices);
    voiceWaitTimer = window.setTimeout(() => {
      clearVoiceWait();
      speakPrepared(token, utterance, onFail);
    }, VOICE_WAIT_MS);
  }, SPEAK_DELAY_MS);
}

function startResume(): void {
  clearResume();
  if (userPaused) {
    return;
  }
  resumeTimer = window.setInterval(() => {
    if (!isSpeechSupported() || userPaused) {
      clearResume();
      return;
    }
    window.speechSynthesis.resume();
  }, RESUME_INTERVAL_MS);
}

/** Whether the browser can speak at all. */
export function isSpeechSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.speechSynthesis?.speak === "function" &&
    typeof window.SpeechSynthesisUtterance === "function"
  );
}

/** The entry currently being read, or null when idle. Shared by every row. */
export function getSpeakingEntryId(): string | null {
  return speakingEntryId;
}

/** The open read-aloud session, including pause and word progress. */
export function getSpeechSnapshot(): SpeechSessionSnapshot {
  return snapshot;
}

/** Subscribe to read-aloud session changes. Returns an unsubscribe. */
export function subscribeSpeech(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getServerSpeechSnapshot(): SpeechSessionSnapshot {
  return IDLE_SNAPSHOT;
}

let voiceDialogActive = false;
const dialogListeners = new Set<Listener>();

function emitDialog(): void {
  for (const listener of dialogListeners) {
    listener();
  }
}

/** True while a hands-free voice conversation owns the shared synthesizer and mic. */
export function getVoiceDialogActive(): boolean {
  return voiceDialogActive;
}

/** Subscribe to voice-conversation activity changes. Returns an unsubscribe. */
export function subscribeVoiceDialogActive(listener: Listener): () => void {
  dialogListeners.add(listener);
  return () => {
    dialogListeners.delete(listener);
  };
}

function voiceDialogServerSnapshot(): boolean {
  return false;
}

/**
 * Mark whether a hands-free voice conversation is in progress. While it is, the
 * per-answer read-aloud control is disabled so a manual toggle cannot cut across
 * the dialog's own playback, discard the completion callback that reopens the
 * mic, or be transcribed by the open microphone and auto-sent.
 */
export function setVoiceDialogActive(active: boolean): void {
  if (voiceDialogActive === active) {
    return;
  }
  voiceDialogActive = active;
  emitDialog();
}

/** Spoken instead of reading a table's cells. */
export const SPEECH_TABLE_CUE = "The data is shown in the table on your screen.";

/** Spoken instead of reading a chart's points. */
export const SPEECH_CHART_CUE = "The data is shown in the chart on your screen.";

/** Spoken instead of reading a bond, quote, or request card. */
export const SPEECH_DETAILS_CUE = "The details are shown on your screen.";

/** A single delimiter cell: optional alignment colons around three or more dashes. */
const TABLE_SEPARATOR_CELL = /^\s*:?-{3,}:?\s*$/;

/**
 * A GFM table delimiter row: one or more `---` cells separated by pipes. A
 * single-column table (`| --- |`) counts, but a bare `---` (a thematic break or
 * setext underline) does not, so it is only a separator when an outer pipe is
 * present or there are at least two cells. Written as a linear scan instead of a
 * backtracking regex.
 */
function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length === 0 || !trimmed.includes("-")) {
    return false;
  }
  const hadOuterPipe = trimmed.startsWith("|") || trimmed.endsWith("|");
  const inner = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const cells = inner.split("|");
  if (cells.length < 2 && !hadOuterPipe) {
    return false;
  }
  return cells.every((cell) => TABLE_SEPARATOR_CELL.test(cell));
}

function cueForVisualType(type: string): string | null {
  switch (type) {
    case "data_table":
      return SPEECH_TABLE_CUE;
    case "curve_chart":
    case "time_series_chart":
      return SPEECH_CHART_CUE;
    case "bond_card":
    case "quote_card":
    case "quote_request_card":
      return SPEECH_DETAILS_CUE;
    default:
      return null;
  }
}

function cueForFence(body: string): string | null {
  const type = /"type"\s*:\s*"([^"]+)"/.exec(body)?.[1];
  if (type === undefined) {
    return null;
  }
  return cueForVisualType(type);
}

function replaceTowerRender(fenceBody: string): string {
  const cue = cueForFence(fenceBody);
  return cue === null ? " " : ` ${cue} `;
}

/** A GFM table is a header row, a dash separator, then rows that contain pipes. */
function replaceMarkdownTables(text: string): string {
  const lines = text.split("\n");
  const kept: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? "";
    const nextIsSeparator = isTableSeparator(lines[index + 1] ?? "");
    if (line.includes("|") && nextIsSeparator) {
      kept.push(SPEECH_TABLE_CUE);
      index += 2;
      while (index < lines.length && (lines[index] ?? "").includes("|")) {
        index += 1;
      }
      continue;
    }
    kept.push(line);
    index += 1;
  }
  return kept.join("\n");
}

function hasMarkdownTable(text: string): boolean {
  const lines = text.split("\n");
  for (let index = 0; index < lines.length - 1; index += 1) {
    const line = lines[index] ?? "";
    if (line.includes("|") && isTableSeparator(lines[index + 1] ?? "")) {
      return true;
    }
  }
  return false;
}

/**
 * True when the reply contains something the user has to see: a table, chart,
 * or bond, quote, or request card. An unfinished fence counts once its type
 * is already in the text.
 */
export function replyHasVisual(markdown: string): boolean {
  const text = markdown.replaceAll("\r\n", "\n");
  const closedFence = / {0,3}```tower-render[^\n]*\n([\s\S]*?)```/g;
  for (const match of text.matchAll(closedFence)) {
    if (cueForFence(match[1] ?? "") !== null) {
      return true;
    }
  }
  const openFence = / {0,3}```tower-render[^\n]*\n([\s\S]*)$/.exec(text);
  if (openFence !== null && cueForFence(openFence[1] ?? "") !== null) {
    return true;
  }
  return hasMarkdownTable(text);
}

function collapseRepeatedCues(text: string): string {
  let collapsed = text;
  for (const cue of [SPEECH_TABLE_CUE, SPEECH_CHART_CUE, SPEECH_DETAILS_CUE]) {
    const escaped = cue.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
    const pattern = new RegExp(String.raw`(?:${escaped}\s*){2,}`, "g");
    collapsed = collapsed.replace(pattern, `${cue} `);
  }
  return collapsed;
}

/**
 * Turn assistant markdown into something `speechSynthesis` can say.
 * Tables, charts, and cards become a short cue instead of their cells.
 * Other fences are dropped; emphasis and links keep their words.
 * An empty string means there is nothing to read.
 */
export function toSpeakableText(markdown: string): string {
  let text = markdown.replaceAll("\r\n", "\n");

  // Named visuals first. Closed fences, then one the model left open.
  text = text.replace(/ {0,3}```tower-render[^\n]*\n([\s\S]*?)```/g, (_, body: string) =>
    replaceTowerRender(body),
  );
  text = text.replace(/ {0,3}```tower-render[^\n]*\n([\s\S]*)$/g, (_, body: string) =>
    replaceTowerRender(body),
  );
  text = text.replace(/```[\s\S]*?```/g, " ");
  text = text.replace(/```[\s\S]*/g, " ");
  text = replaceMarkdownTables(text);

  text = text.replace(/!?\[([^\][\n]*)]\(([^()\n]*)\)/g, "$1");

  text = text.replace(/^#{1,6}\s+/gm, "");
  text = text.replace(/^>\s?/gm, "");
  text = text.replace(/^[ \t]*[-*+][ \t]+/gm, "");
  text = text.replace(/^[ \t]*\d+\.[ \t]+/gm, "");
  text = text.replace(/^[ \t]*([-*_])\1{2,}[ \t]*$/gm, " ");

  text = text.replace(/[*_`~|]+/g, "");

  text = text.replace(/\s+/g, " ");
  text = collapseRepeatedCues(text);
  text = text.replace(/[ \t]{2,}/g, " ");
  return text.trim();
}

function finish(token: number): void {
  if (token !== generation) {
    return;
  }
  clearResume();
  userPaused = false;
  const hadEntry = speakingEntryId !== null;
  clearEntrySession();
  if (hadEntry) {
    publish();
  }
}

function noteBoundary(token: number, charIndex: number): void {
  if (token !== generation || sessionText.length === 0) {
    return;
  }
  const absolute = Math.min(sessionText.length, Math.max(0, sessionOffset + charIndex));
  sessionProgress = absolute / sessionText.length;
  // The clock must continue from this character, not add the same stretch again.
  if (progressTimer !== null) {
    progressOrigin = absolute;
    progressStartedAt = Date.now();
  }
  publish();
}

function attachUtterance(token: number, text: string): SpeechSynthesisUtterance {
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = SPEECH_LANG;
  utterance.onboundary = (event: SpeechSynthesisEvent) => {
    noteBoundary(token, event.charIndex);
  };
  utterance.onend = () => {
    finish(token);
  };
  utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
    // Chrome can report a pause as an interruption. That must not close the
    // session, or resume would have nothing left to continue.
    if (userPaused && event.error === "interrupted") {
      return;
    }
    finish(token);
  };
  return utterance;
}

/**
 * Speak `text` from a character offset. The offset snaps to a word. Reaching
 * the end closes the session. A previous utterance is cancelled only after the
 * generation token advances, so its `onend` cannot hide the transport controls.
 */
function beginReadAloud(
  conversationId: string,
  entryId: string,
  text: string,
  offset: number,
): void {
  if (text.length === 0) {
    return;
  }
  const sliceFrom = snapToWord(text, offset);
  if (sliceFrom >= text.length) {
    stopSpeech();
    return;
  }

  generation += 1;
  const token = generation;
  userPaused = false;
  held = false;
  clearVoiceWait();
  if (speakingEntryId !== null || greetingSpeaking) {
    cancelSpeech();
  }
  clearResume();
  clearGreeting();
  clearProgressClock();

  sessionText = text;
  sessionOffset = sliceFrom;
  sessionProgress = sliceFrom / text.length;
  speakingEntryId = entryId;
  sessionConversationId = conversationId;
  publish();

  scheduleSpeak(token, attachUtterance(token, text.slice(sliceFrom)), () => {
    finish(token);
  });
}

/** True while the voice-dialog greeting is being spoken. Not a chat entry. */
export function getGreetingSpeaking(): boolean {
  return greetingSpeaking;
}

/** Subscribe to voice-dialog greeting changes. Returns an unsubscribe. */
export function subscribeGreeting(listener: Listener): () => void {
  greetingListeners.add(listener);
  return () => {
    greetingListeners.delete(listener);
  };
}

function clearGreeting(): void {
  if (!greetingSpeaking) {
    return;
  }
  greetingSpeaking = false;
  emitGreeting();
}

/**
 * Pause the utterance in place. The Chrome keepalive is stopped so it cannot
 * call `resume()` under the user. `onend` does not fire until playback continues.
 */
export function pauseSpeech(): void {
  if (!isSpeechSupported()) {
    return;
  }
  held = false;
  userPaused = true;
  clearResume();
  clearProgressClock();
  window.speechSynthesis.pause();
  publish();
}

/** Pause because the owning chat is not on screen. A user pause is left alone. */
function holdSpeech(): void {
  if (held || userPaused || speakingEntryId === null || !isSpeechSupported()) {
    return;
  }
  held = true;
  clearResume();
  clearProgressClock();
  window.speechSynthesis.pause();
  publish();
}

/** Continue after the owning chat is back, unless the user had paused. */
function releaseHold(): void {
  if (!held) {
    return;
  }
  held = false;
  if (userPaused || speakingEntryId === null || !isSpeechSupported()) {
    publish();
    return;
  }
  window.speechSynthesis.resume();
  startResume();
  startProgressClock();
  publish();
}

/**
 * The chat now on screen. Leaving the chat that is being read holds playback.
 * Coming back continues it, unless the user paused before leaving.
 */
export function setVisibleConversation(id: string | null): void {
  if (visibleConversationId === id) {
    return;
  }
  visibleConversationId = id;
  if (speakingEntryId === null || sessionConversationId === null) {
    return;
  }
  if (id !== sessionConversationId) {
    holdSpeech();
    return;
  }
  releaseHold();
}

function getVisibleConversationId(): string | null {
  return visibleConversationId;
}

function getSessionConversationId(): string | null {
  return sessionConversationId;
}

/** Continue a user-paused utterance from the same word. */
export function resumeSpeech(): void {
  if (!userPaused) {
    return;
  }
  userPaused = false;
  if (isSpeechSupported()) {
    window.speechSynthesis.resume();
    if (speakingEntryId !== null || greetingSpeaking) {
      startResume();
    }
    startProgressClock();
  }
  publish();
}

/** Stop whatever is being read, including the voice-dialog greeting. */
export function stopSpeech(): void {
  generation += 1;
  userPaused = false;
  held = false;
  clearResume();
  clearVoiceWait();
  if (isSpeechSupported()) {
    cancelSpeech();
  }
  const hadEntry = speakingEntryId !== null;
  clearEntrySession();
  if (hadEntry) {
    publish();
  }
  clearGreeting();
}

/**
 * Speak `text` once and call `onDone` when it finishes or cannot play.
 * A later `stopSpeech` cancels it without calling `onDone`. This is not tied
 * to a chat entry, so message rows do not show as speaking.
 */
export function speakOnce(text: string, onDone: () => void): void {
  const trimmed = text.trim();
  if (!isSpeechSupported() || trimmed.length === 0) {
    onDone();
    return;
  }

  // Keep the click-unlocked AudioContext running so a later reply (after a
  // network wait) can still play on iOS Safari. Missing context is a no-op.
  primeListenCue();

  generation += 1;
  const token = generation;
  userPaused = false;
  held = false;
  clearVoiceWait();
  if (speakingEntryId !== null || greetingSpeaking) {
    cancelSpeech();
  }
  clearResume();
  if (speakingEntryId !== null) {
    clearEntrySession();
    publish();
  }

  let notified = false;
  const finishGreeting = () => {
    if (token !== generation || notified) {
      return;
    }
    notified = true;
    clearResume();
    clearGreeting();
    onDone();
  };

  const utterance = new SpeechSynthesisUtterance(trimmed);
  utterance.lang = SPEECH_LANG;
  utterance.onend = () => {
    finishGreeting();
  };
  utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
    // Chrome can report a pause as an interruption. That must not finish the
    // greeting, or resume would have nothing left to continue.
    if (userPaused && event.error === "interrupted") {
      return;
    }
    finishGreeting();
  };

  greetingSpeaking = true;
  emitGreeting();

  scheduleSpeak(token, utterance, finishGreeting);
}

/**
 * Read `markdown` as this entry. A second press pauses in place; the next one
 * resumes from the same word. A later call for another entry replaces this one.
 */
export function toggleSpeech(conversationId: string, entryId: string, markdown: string): void {
  if (!isSpeechSupported() || voiceDialogActive) {
    return;
  }
  if (speakingEntryId === entryId && sessionConversationId === conversationId) {
    if (userPaused) {
      resumeSpeech();
      return;
    }
    pauseSpeech();
    return;
  }
  const text = toSpeakableText(markdown);
  if (text.length === 0) {
    return;
  }
  beginReadAloud(conversationId, entryId, text, 0);
}

/**
 * Jump within the open entry and keep playing. `ratio` is 0 at the first word
 * and 1 at the end, which closes the session.
 */
export function seekSpeech(conversationId: string, entryId: string, ratio: number): void {
  if (!isSpeechSupported() || voiceDialogActive) {
    return;
  }
  if (
    speakingEntryId !== entryId ||
    sessionConversationId !== conversationId ||
    sessionText.length === 0
  ) {
    return;
  }
  const index = Math.floor(clampRatio(ratio) * sessionText.length);
  beginReadAloud(conversationId, entryId, sessionText, index);
}

/**
 * Tooltip copy for the read-aloud control. The button's accessible name matches
 * it: "Read aloud", "Pause", or "Play".
 */
export function describeSpeechState(
  speech: Pick<SpeechPlayback, "isSupported" | "canSpeak" | "speaking" | "paused">,
): string {
  if (!speech.isSupported) {
    return "Read aloud is not supported in this browser";
  }
  if (voiceDialogActive && !speech.speaking) {
    return "Read aloud is paused during a voice conversation";
  }
  if (!speech.canSpeak) {
    return "Nothing to read aloud";
  }
  if (speech.speaking && speech.paused) {
    return "Play";
  }
  if (speech.speaking) {
    return "Pause";
  }
  return "Read aloud";
}

/**
 * Read-aloud state for one assistant entry in one chat. The same turn id exists
 * in every conversation, so the transport belongs to the chat that started it.
 * Unmount stops it only while that chat is still on screen.
 */
export function useSpeechPlayback(
  conversationId: string | null,
  entryId: string,
  markdown: string,
): SpeechPlayback {
  const session = useSyncExternalStore(subscribeSpeech, getSpeechSnapshot, getServerSpeechSnapshot);
  const dialogActive = useSyncExternalStore(
    subscribeVoiceDialogActive,
    getVoiceDialogActive,
    voiceDialogServerSnapshot,
  );
  const active =
    conversationId !== null &&
    session.entryId === entryId &&
    session.conversationId === conversationId;
  // A live voice conversation owns the synthesizer and mic, so the manual
  // per-answer control is unavailable until it ends (unless this very entry is
  // the one being read, so the control stays available).
  const canSpeak = toSpeakableText(markdown).length > 0 && (!dialogActive || active);

  useEffect(() => {
    return () => {
      if (conversationId === null) {
        return;
      }
      if (getSpeakingEntryId() !== entryId || getSessionConversationId() !== conversationId) {
        return;
      }
      if (getVisibleConversationId() !== conversationId) {
        return;
      }
      stopSpeech();
    };
  }, [conversationId, entryId]);

  return {
    isSupported: isSpeechSupported(),
    canSpeak,
    speaking: active,
    playing: active && !session.paused,
    paused: active && session.paused,
    active,
    progress: active ? session.progress : 0,
    toggle: () => {
      if (conversationId === null) {
        return;
      }
      toggleSpeech(conversationId, entryId, markdown);
    },
    seek: (ratio: number) => {
      if (conversationId === null) {
        return;
      }
      seekSpeech(conversationId, entryId, ratio);
    },
  };
}
